import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthenticationLocalService } from '../../features/authentication/data/data_source/local/authentication_local_service';
import { useEffect, useRef } from 'react';
import { useGetMyUserProfile } from '../../core/components/user/domain/hooks/use_get_user_profile';
import LoadingPage from '../../core/components/user/presentation/loading_page';
import { useLogout } from '../../core/components/user/domain/hooks/use_logout';
import { getCurrentLanguage, isSupportedLanguage, setLanguage } from '../../i18n/config';

interface PrivateRouteProps {
  loginLocation: string;
}

function PrivateRoute({loginLocation}: PrivateRouteProps) {
    const authLocalService = new AuthenticationLocalService();
    const location = useLocation();
    const { mutate: logout, logoutLocal } = useLogout();
    
    const tokenChecker = () => {
      const token = authLocalService.getAccessToken();
      if (!token) {
        authLocalService.removeRefreshToken();
        return null;
      }
      return token;
    };

    const token = tokenChecker();
    
    const { 
      data: userProfile, 
      error, 
      isLoading, 
      refetch 
    } = useGetMyUserProfile(!!token);


    // Latest token/userProfile read without re-running the effect: it must only run when refetch changes.
    const refetchGuardRef = useRef({ token, userProfile });
    useEffect(() => {
      refetchGuardRef.current = { token, userProfile };
    }, [token, userProfile]);

    useEffect(() => {
      const { token: currentToken, userProfile: currentUserProfile } = refetchGuardRef.current;
      if (currentToken && currentUserProfile) {
        refetch();
      }
    }, [refetch]);

    useEffect(() => {
      if (error && error.response?.status === 404) {
        logout();
      }
    }, [error, logout]);

    useEffect(() => {
      if (location.pathname.includes('/end') && token) {
        refetch();
      }
    }, [location.pathname, token, refetch]);

    // La langue enregistrée sur le compte prime sur celle du navigateur.
    const userLanguage = userProfile?.language;
    useEffect(() => {
      if (isSupportedLanguage(userLanguage) && userLanguage !== getCurrentLanguage()) {
        setLanguage(userLanguage);
      }
    }, [userLanguage]);

    useEffect(() => {
      if (!token && userProfile) {
        logoutLocal();
      }
    }, [token, userProfile, logoutLocal]);

    const needEmailVerification: boolean = (userProfile?.company?.needEmailVerified ?? false) === true ? userProfile?.verified === false : false;

    return token != null
        ? userProfile && needEmailVerification === false && userProfile?.banned === false
            ? <Outlet context={{...userProfile, getUserProfile: refetch}}/>
            : <LoadingPage 
                verified={userProfile?.verified} 
                revalidationRequested={userProfile?.revalidationRequestedAt != null}
                email={userProfile?.email} 
                error={error !== null} 
                onRetry={refetch} 
                isLoading={isLoading}
                banned={userProfile?.banned}
              />
        : <Navigate to={loginLocation} state={{from: location}} replace/>;
}

export default PrivateRoute;
